import { CheckBadgeIcon } from "@heroicons/react/20/solid";
import { QrCodeIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { useRouter } from "router2";

import { useDebouncedValue } from "~/hook/base/useDebouncedValue.ts";
import { useAppConfig } from "~/hook/domain/useAppConfig.ts";
import { usePoke } from "~/hook/domain/usePoke.ts";
import { User } from "~/service/dataType.ts";
import { dataUpdatedAtMiddleware } from "~/service/swr/middleware.dataUpdatedAt.ts";
import { createShouldAnimateMiddleware } from "~/service/swr/middleware.shouldAnimate.ts";
import { createUseSWRMiddleware } from "~/service/swr/middleware.ts";
import { isVerifiedUser } from "~/service/util.ts";
import { AppLink } from "~/ui/base/AppLink.tsx";
import { Image } from "~/ui/base/Image.tsx";
import { UserListItem } from "~/ui/domain/UserListItem.tsx";
import { WebLayout } from "~/ui/layout/WebLayout.tsx";
import { PokeWithAudioSheet } from "~/ui/overlay/PokeWithAudioSheet.tsx";
import { PokeWithDrawingSheet } from "~/ui/overlay/PokeWithDrawingSheet.tsx";
import { PokeWithEmojiSheet } from "~/ui/overlay/PokeWithEmojiSheet.tsx";
import { PokeWithGeoLocationSheet } from "~/ui/overlay/PokeWithGeolocationSheet.tsx";
import { PokeWithPictureSheet } from "~/ui/overlay/PokeWithPictureSheet.tsx";
import { QrScannerSheet } from "~/ui/overlay/QrScannerSheet.tsx";
import { useAuthNavigator } from "~/ui/provider/Auth.tsx";
import { Layer, useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const cx = {
  pokeOptionButton:
    "w-full rounded-full bg-black py-3 px-5 text-center font-medium text-white duration-200 active:opacity-60 disabled:bg-zinc-300 dark:bg-zinc-800 dark:disabled:bg-zinc-900",
};

const SEARCH_TEXT_KEY = "q";

type Key = [string, { email: string; limit: number; name: string }];

const isEqualKey = (a: Key, b: Key) =>
  a[1].email === b[1].email && a[1].name === b[1].name;

const useSWRMiddleware = createUseSWRMiddleware(
  createShouldAnimateMiddleware(isEqualKey),
  dataUpdatedAtMiddleware,
);

export const WebSearch = () => {
  useAuthNavigator({ goToAuth: "/web/sign-in" });
  const overlay = useStackedLayer();
  const { t } = useLingui();
  const { params, replace } = useRouter();

  const searchText = params[SEARCH_TEXT_KEY] ?? "";
  const setSearchText = (text: string) => {
    replace({
      pathname: "/web/search",
      ...(text && { query: { [SEARCH_TEXT_KEY]: text } }),
    });
  };

  const deferredSearchText = useDebouncedValue(searchText, 300);
  const [selected, setSelected] = useState<null | User>(null);

  const { data, dataUpdatedAt, isLoading, shouldAnimate } = useSWRMiddleware<
    User[],
    unknown
  >(
    [
      "user",
      { email: deferredSearchText, limit: 10, name: deferredSearchText },
    ],
    {
      keepPreviousData: true,
      onSuccess: () => {
        setSelected(null);
      },
    },
  );

  const { isMutating, trigger } = usePoke();
  const {
    isAudioPokeEnabled,
    isDrawingPokeEnabled,
    isEmojiPokeEnabled,
    isGeolocationPokeEnabled,
    isNormalPokeEnabled,
    isPicturePokeEnabled,
    isPokeEnabled,
  } = useAppConfig();

  const openSheet = (Sheet: Layer<{ email: string }>) => {
    if (typeof selected?.email !== "string") return;
    overlay(Sheet, { email: selected.email });
  };

  const pokeOptions = [
    {
      enabled: isNormalPokeEnabled,
      key: "normal",
      label: <Trans>바로 콕! 찌르기 👉</Trans>,
      onClick: () => {
        if (typeof selected?.email === "string") {
          void trigger({
            email: selected.email,
            payload: { type: "normal" },
          }).then(() => {
            setSelected(null);
          });
        }
      },
    },
    {
      enabled: isAudioPokeEnabled,
      key: "audio",
      label: <Trans>소리 찌르기 🎙️</Trans>,
      onClick: () => {
        openSheet(PokeWithAudioSheet);
      },
    },
    {
      enabled: isPicturePokeEnabled,
      key: "picture",
      label: <Trans>사진 찌르기 📸</Trans>,
      onClick: () => {
        openSheet(PokeWithPictureSheet);
      },
    },
    {
      enabled: isDrawingPokeEnabled,
      key: "drawing",
      label: <Trans>그림 찌르기 🎨</Trans>,
      onClick: () => {
        openSheet(PokeWithDrawingSheet);
      },
    },
    {
      enabled: isGeolocationPokeEnabled,
      key: "geolocation",
      label: <Trans>내 위치 찌르기 📍</Trans>,
      onClick: () => {
        openSheet(PokeWithGeoLocationSheet);
      },
    },
    {
      enabled: isEmojiPokeEnabled,
      key: "emoji",
      label: <Trans>이모티콘 찌르기 😊</Trans>,
      onClick: () => {
        openSheet(PokeWithEmojiSheet);
      },
    },
  ].filter((option) => option.enabled);

  return (
    <WebLayout>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
        {/* Left Column: Search Input & User List */}
        <div className="md:col-span-7">
          <div className="flex justify-between">
            <p className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">
              <Trans>누구를 콕콕! 찌를까요?</Trans>
            </p>
            <button
              className="text-zinc-500 dark:text-zinc-400"
              onClick={() => {
                overlay(QrScannerSheet);
              }}
              type="button"
            >
              <QrCodeIcon className="size-6" />
            </button>
          </div>

          <div className="flex items-center pt-8">
            <span className="block w-5 text-xl font-bold">@</span>
            <input
              autoCapitalize="off"
              className="flex-1 rounded-none border-b-2 border-black bg-transparent py-2 text-xl font-bold outline-hidden placeholder:font-normal dark:border-white"
              data-testid="검색어"
              onChange={({ target: { value } }) => {
                setSearchText(value);
              }}
              placeholder={t`이름 또는 아이디로 검색해요`}
              type="text"
              value={searchText}
            />
          </div>

          <div className="mt-5 flex flex-col" data-testid="유저 컨테이너">
            {data?.map((user, i) => (
              <UserListItem
                animation={shouldAnimate ? { delayTimes: i } : null}
                isVerifiedUser={isVerifiedUser(user)}
                key={user.email + String(dataUpdatedAt)}
                onClick={() => {
                  setSelected(user);
                }}
                selected={selected?.email === user.email}
                userEmail={user.email}
                userName={user.name}
                userProfileImageUrl={user.profileImageUrl}
              />
            ))}
            {data?.length === 0 && (
              <div className="animate-from-bottom flex flex-col items-center pt-16 text-zinc-600 dark:text-zinc-400">
                <span className="text-zinc-400 dark:text-zinc-500">
                  <XCircleIcon className="size-6" />
                </span>
                <p className="pt-6">
                  <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                    @{deferredSearchText}
                  </span>{" "}
                  <Trans>유저를 찾지 못했어요.</Trans>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Selected User & Poke Options */}
        <div className="md:col-span-5">
          <div className="flex flex-col gap-4 p-2 md:sticky md:top-10">
            {selected ? (
              <>
                <AppLink
                  className="flex items-center gap-3 pb-4 duration-150 hover:opacity-80"
                  pathname={`/web/user/${selected.email}`}
                >
                  <Image
                    alt={t`프로필 이미지`}
                    size={48}
                    src={
                      selected.profileImageUrl ??
                      "/asset/default_user_profile.png"
                    }
                    transitionName="web-search-selected-user"
                  />
                  <div>
                    <p className="flex items-center text-lg font-bold text-zinc-900 dark:text-zinc-100">
                      @{selected.email}
                      {isVerifiedUser(selected) && (
                        <span className="ml-1 text-blue-500">
                          <CheckBadgeIcon className="size-4" />
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-zinc-500">{selected.name}</p>
                  </div>
                </AppLink>

                {isPokeEnabled && (
                  <div className="flex flex-col gap-3 pt-2">
                    {pokeOptions.map((option) => (
                      <button
                        className={cx.pokeOptionButton}
                        disabled={isLoading || isMutating}
                        key={option.key}
                        onClick={option.onClick}
                        type="button"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center text-zinc-500 dark:text-zinc-400">
                <p className="font-medium">
                  <Trans>상대를 선택해주세요</Trans>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </WebLayout>
  );
};
